import React, { useState, useEffect } from 'react';
import { TextField } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { CloseIcon, ArrowRightIcon, CheckIcon } from '../../assets/icons';
import { useI18n } from '../../i18n';

interface GetInTouchModalProps {
  open: boolean;
  onClose: () => void;
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  needs: string[];
  companyName: string;
  companyDescription: string;
}

export const GetInTouchModal: React.FC<GetInTouchModalProps> = ({ open, onClose }) => {
  const { t } = useI18n();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    needs: [],
    companyName: '',
    companyDescription: '',
  });

  const totalSteps = 3;
  const progress = (step / totalSteps) * 100;

  useEffect(() => {
    if (open) {
      setStep(1);
      setSubmitError(null);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        needs: [],
        companyName: '',
        companyDescription: '',
      });
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const response = await fetch('/api/get-in-touch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Submission failed');
      }

      onClose();
    } catch {
      setSubmitError(t.contact.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && canProceed()) {
      e.preventDefault();
      handleNext();
    }
  };

  const updateField = (field: keyof Omit<FormData, 'needs'>, value: string) => {
    setFormData({ ...formData, [field]: value });
  };

  const toggleNeed = (need: string) => {
    setFormData((prev) => ({
      ...prev,
      needs: prev.needs.includes(need)
        ? prev.needs.filter((n) => n !== need)
        : [...prev.needs, need],
    }));
  };

  const canProceed = (): boolean => {
    switch (step) {
      case 1:
        return !!formData.firstName && !!formData.lastName && !!formData.email;
      case 2:
        return formData.needs.length > 0;
      case 3:
        return !!formData.companyName && !!formData.companyDescription;
      default:
        return false;
    }
  };

  const needOptions = [
    { value: 'screening', label: t.contact.step2.options.screening },
    { value: 'sourcing', label: t.contact.step2.options.sourcing },
    { value: 'interview', label: t.contact.step2.options.interview },
    { value: 'caseStudies', label: t.contact.step2.options.caseStudies },
  ];

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6" onKeyDown={handleKeyDown}>
            <div>
              <h2 className="font-display text-3xl md:text-4xl text-white mb-3">{t.contact.step1.title}</h2>
              <p className="text-white/50 text-lg">{t.contact.step1.subtitle}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <TextField
                fullWidth
                label={t.contact.step1.firstName}
                value={formData.firstName}
                onChange={(e) => updateField('firstName', e.target.value)}
                required
                autoFocus
                sx={textFieldStyles}
              />
              <TextField
                fullWidth
                label={t.contact.step1.lastName}
                value={formData.lastName}
                onChange={(e) => updateField('lastName', e.target.value)}
                required
                sx={textFieldStyles}
              />
            </div>
            <TextField
              fullWidth
              label={t.contact.step1.email}
              type="email"
              value={formData.email}
              onChange={(e) => updateField('email', e.target.value)}
              required
              sx={textFieldStyles}
            />
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="font-display text-3xl md:text-4xl text-white mb-3">{t.contact.step2.title}</h2>
              <p className="text-white/50 text-lg">{t.contact.step2.subtitle}</p>
            </div>
            <div className="space-y-3">
              {needOptions.map((option) => {
                const isSelected = formData.needs.includes(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => toggleNeed(option.value)}
                    className={`w-full flex items-center gap-4 px-6 py-5 rounded-xl border text-left transition-all duration-200 ${
                      isSelected
                        ? 'border-[var(--accent-1)]/40 bg-[var(--accent-1)]/10'
                        : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.05]'
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded flex items-center justify-center border transition-all duration-200 flex-shrink-0 ${
                        isSelected
                          ? 'bg-[var(--accent-1)] border-[var(--accent-1)]'
                          : 'border-white/30'
                      }`}
                    >
                      {isSelected && <CheckIcon className="w-3.5 h-3.5 text-black" />}
                    </div>
                    <span className="text-white text-lg">{option.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6" onKeyDown={handleKeyDown}>
            <div>
              <h2 className="font-display text-3xl md:text-4xl text-white mb-3">{t.contact.step3.title}</h2>
              <p className="text-white/50 text-lg">{t.contact.step3.subtitle}</p>
            </div>
            <TextField
              fullWidth
              label={t.contact.step3.companyName}
              value={formData.companyName}
              onChange={(e) => updateField('companyName', e.target.value)}
              required
              autoFocus
              sx={textFieldStyles}
            />
            <TextField
              fullWidth
              multiline
              rows={4}
              label={t.contact.step3.companyDescription}
              value={formData.companyDescription}
              onChange={(e) => updateField('companyDescription', e.target.value)}
              required
              sx={textFieldStyles}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[10000] bg-black flex flex-col"
          initial={{ opacity: 0, y: '100%' }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: '100%' }}
          transition={{ duration: 0.5, ease: [0.32, 0.72, 0, 1] }}
        >
          {/* Progress bar - top */}
          <motion.div
            className="w-full h-1 bg-white/10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.3 }}
          >
            <motion.div
              className="h-full bg-[var(--accent-1)]"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.4, ease: 'easeOut', delay: 0.5 }}
            />
          </motion.div>

          {/* Top bar: step counter + close */}
          <motion.div
            className="flex items-center justify-between px-6 md:px-12 py-5"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.4, ease: 'easeOut' }}
          >
            <div className="text-white/40 text-sm font-medium">
              {t.contact.navigation.step} {step} {t.contact.navigation.of} {totalSteps}
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
            >
              <CloseIcon className="w-5 h-5 text-white/70" />
            </button>
          </motion.div>

          {/* Content - centered */}
          <div className="flex-1 flex items-center justify-center px-6 md:px-12 overflow-y-auto">
            <motion.div
              className="w-full max-w-lg"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45, duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
            >
              <AnimatePresence exitBeforeEnter>
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  transition={{ duration: 0.3, ease: 'easeOut' }}
                >
                  {renderStep()}
                </motion.div>
              </AnimatePresence>
            </motion.div>
          </div>

          {/* Bottom bar: navigation */}
          <motion.div
            className="px-6 md:px-12 py-6 border-t border-white/10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.4, ease: 'easeOut' }}
          >
            <div className="max-w-lg mx-auto flex items-center justify-between">
              <div>
                {step > 1 && (
                  <button
                    onClick={handleBack}
                    disabled={isSubmitting}
                    className="text-white/50 hover:text-white text-sm font-medium transition-colors disabled:opacity-30"
                  >
                    ← {t.contact.navigation.back}
                  </button>
                )}
              </div>
              <div className="flex items-center gap-4">
                {step === 1 && (
                  <span className="text-white/30 text-xs hidden sm:block">
                    {t.contact.navigation.pressEnter}
                  </span>
                )}
                <button
                  onClick={handleNext}
                  disabled={!canProceed() || isSubmitting}
                  className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-white hover:bg-white/90 text-black font-semibold text-sm transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  {step === totalSteps ? (
                    isSubmitting ? (
                      t.contact.navigation.sending
                    ) : (
                      <>
                        {t.contact.navigation.send}
                        <CheckIcon className="w-4 h-4" />
                      </>
                    )
                  ) : (
                    <>
                      {t.contact.navigation.continue}
                      <ArrowRightIcon className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </div>

            {submitError && (
              <p className="text-red-400 text-sm mt-3 text-center max-w-lg mx-auto">
                {t.contact.error}
              </p>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const textFieldStyles = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderRadius: '12px',
    '& fieldset': {
      borderColor: 'rgba(255,255,255,0.15)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(255,255,255,0.25)',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'var(--accent-1)',
    },
  },
  '& .MuiInputBase-input': {
    color: 'white',
    fontSize: '1.05rem',
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255,255,255,0.5)',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: 'var(--accent-1)',
  },
};
